import { supabase } from '../lib/supabase';
import { MAIN_SITE_PASSWORD } from '../lib/mainSiteAccess';

export type PlatformSiteSettings = {
  main_site_password_lock_enabled: boolean;
  main_site_access_code: string;
  updated_at: string;
};

const ROW_ID = 'global';
const CACHE_KEY = 'd88_platform_site_settings_v1';

export function readCachedSiteSettings(): PlatformSiteSettings | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PlatformSiteSettings;
  } catch {
    return null;
  }
}

export function writeCachedSiteSettings(settings: PlatformSiteSettings): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}

export function broadcastSiteAccessChanged(): void {
  try {
    window.dispatchEvent(new Event('d88-site-access-changed'));
  } catch {
    /* ignore */
  }
}

/** Default: lock ON with District2026 unless staff disables in Operations. */
export function defaultSiteSettings(): PlatformSiteSettings {
  return {
    main_site_password_lock_enabled: true,
    main_site_access_code: MAIN_SITE_PASSWORD,
    updated_at: new Date().toISOString(),
  };
}

export const siteSettingsService = {
  async getPlatformSettings(): Promise<{ data: PlatformSiteSettings; error: null } | { data: null; error: Error }> {
    const { data, error } = await supabase
      .from('platform_site_settings')
      .select('main_site_password_lock_enabled, main_site_access_code, updated_at')
      .eq('id', ROW_ID)
      .maybeSingle();

    if (error || !data) {
      return { data: null, error: error ?? new Error('Platform settings not found') };
    }

    const normalized: PlatformSiteSettings = {
      main_site_password_lock_enabled: !!data.main_site_password_lock_enabled,
      main_site_access_code: String(data.main_site_access_code || MAIN_SITE_PASSWORD),
      updated_at: data.updated_at ?? new Date().toISOString(),
    };
    writeCachedSiteSettings(normalized);
    return { data: normalized, error: null };
  },

  async updatePlatformSettings(
    patch: Partial<Pick<PlatformSiteSettings, 'main_site_password_lock_enabled' | 'main_site_access_code'>>
  ) {
    const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (typeof patch.main_site_password_lock_enabled === 'boolean') {
      payload.main_site_password_lock_enabled = patch.main_site_password_lock_enabled;
    }
    if (typeof patch.main_site_access_code === 'string') {
      payload.main_site_access_code = patch.main_site_access_code.trim() || MAIN_SITE_PASSWORD;
    }

    const { data, error } = await supabase
      .from('platform_site_settings')
      .update(payload)
      .eq('id', ROW_ID)
      .select('main_site_password_lock_enabled, main_site_access_code, updated_at')
      .maybeSingle();

    if (error) return { data: null, error };
    if (data) {
      const normalized: PlatformSiteSettings = {
        main_site_password_lock_enabled: !!data.main_site_password_lock_enabled,
        main_site_access_code: String(data.main_site_access_code || MAIN_SITE_PASSWORD),
        updated_at: data.updated_at ?? new Date().toISOString(),
      };
      writeCachedSiteSettings(normalized);
      broadcastSiteAccessChanged();
      return { data: normalized, error: null };
    }
    return { data: null, error: new Error('Update returned no row') };
  },
};
